/**
 * useAuth Hook - 认证相关 Hook
 * 提供登录、注册、登出等功能
 */
import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import * as authService from '../services/auth'
import { parseApiError } from '../services/api'

/**
 * 认证 Hook
 */
export function useAuth() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    user,
    isAuthenticated,
    isLoading: storeLoading,
    error: storeError,
    login: storeLogin,
    logout: storeLogout,
    setError,
    clearError,
    initialize
  } = useAuthStore()
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // 初始化认证状态
  useEffect(() => {
    initialize()
  }, [initialize])
  
  /**
   * 用户注册
   * 注意：注册成功后不自动跳转，由调用者决定跳转目标（支持 redirect 参数）
   */
  const register = useCallback(async (
    username: string,
    email: string,
    password: string
  ) => {
    setIsSubmitting(true)
    clearError()
    
    try {
      const response = await authService.register({ username, email, password })
      // 清除所有缓存数据（确保新用户看到自己的数据）
      queryClient.clear()
      storeLogin(response.user, response.accessToken)
      // 不自动跳转，让调用者处理（支持 redirect 参数）
      return response
    } catch (error) {
      const apiError = parseApiError(error)
      setError(apiError.message)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [storeLogin, setError, clearError, queryClient])
  
  /**
   * 用户登录
   * 注意：登录成功后不自动跳转，由调用者决定跳转目标（支持 redirect 参数）
   */
  const login = useCallback(async (email: string, password: string) => {
    setIsSubmitting(true)
    clearError()
    
    try {
      const response = await authService.login({ email, password })
      // 清除所有缓存数据（确保切换用户后看到正确的数据）
      queryClient.clear()
      storeLogin(response.user, response.accessToken)
      // 不自动跳转，让调用者处理（支持 redirect 参数）
      return response
    } catch (error) {
      const apiError = parseApiError(error)
      setError(apiError.message)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [storeLogin, setError, clearError, queryClient])
  
  /**
   * 用户登出
   */
  const logout = useCallback(() => {
    // 清除所有缓存数据
    queryClient.clear()
    storeLogout()
    navigate('/')
  }, [storeLogout, navigate, queryClient])
  
  /**
   * 检查用户名可用性
   */
  const checkUsernameAvailable = useCallback(async (username: string) => {
    try {
      const result = await authService.checkUsernameAvailable(username)
      return result.available
    } catch {
      return false
    }
  }, [])
  
  /**
   * 检查邮箱可用性
   */
  const checkEmailAvailable = useCallback(async (email: string) => {
    try {
      const result = await authService.checkEmailAvailable(email)
      return result.available
    } catch {
      return false
    }
  }, [])
  
  /**
   * 刷新用户信息
   */
  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser()
      useAuthStore.getState().setUser(user)
      return user
    } catch (error) {
      // 如果刷新失败，可能 token 已过期
      const apiError = parseApiError(error)
      if (apiError.status === 401) {
        storeLogout()
        navigate('/login')
      }
      throw error
    }
  }, [storeLogout, navigate])
  
  return {
    // 状态
    user,
    isAuthenticated,
    isLoading: storeLoading || isSubmitting,
    error: storeError,
    
    // 动作
    register,
    login,
    logout,
    checkUsernameAvailable,
    checkEmailAvailable,
    refreshUser,
    clearError
  }
}

export default useAuth

